import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  Renderer2,
} from '@angular/core';

import { TuiDataListWrapper, TuiSelect, TuiChevron } from '@taiga-ui/kit';
import { TuiDataList, TuiDropdown, TuiTextfield } from '@taiga-ui/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Product } from '@core/types/Product';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { defer, map } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { Languages } from '@core/types/Languages';
import { RequestService } from '@core/services/request/request.service';
import { stringifyFn } from '@core/helpers/stringifyFn';

@Component({
  selector: 'x-request-product-selector',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RequestProductSelectorComponent),
      multi: true,
    },
  ],
  imports: [
    TuiTextfield,
    TuiChevron,
    AsyncPipe,
    FormsModule,
    TuiSelect,
    TuiDataList,
    TuiDataListWrapper,
    TuiDropdown,
  ],
  templateUrl: './request-product-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestProductSelectorComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  private onTouched!: (value: any) => void;
  private onChange!: (value: any) => void;
  value: any;
  isDisabled = false;

  private readonly requestApiService = inject(RequestsApiService);
  private readonly requestService = inject(RequestService);
  private readonly $request = this.requestService.$request;

  readonly renderer = inject(Renderer2);
  readonly elementRef = inject(ElementRef);
  private tuiWrapperElement!: HTMLDivElement;
  private intervalId: any = null;
  readonly stringifyFn = stringifyFn;
  $formControl = input.required<FormControl>({ alias: 'formControl' });

  availableProducts$ = defer(() =>
    this.requestApiService.getAvailableProducts$({
      forRequestId: this.$request().id,
    }),
  ).pipe(
    map(products => {
      const productsIdsOfRequest = (this.$request().products || []).map(
        requestProduct => requestProduct.requestProductId.id,
      );

      return products
        .reduce<Product[]>((acc, product) => {
          if (!productsIdsOfRequest.includes(product.id)) {
            acc.push(product);
          }

          return acc;
        }, [])
        .sort((a, b) => {
          return a.name.localeCompare(b.name, [
            Languages.ENGLISH,
            Languages.RUSSIAN,
          ]);
        });
    }),
  );

  ngAfterViewInit(): void {
    this.setTuiWrapperElement();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    this.value = value;
  }

  valueChanged(value: any) {
    this.onChange?.(value);
    this.onTouched?.(value);

    if (this.$formControl().invalid) {
      this.renderer.addClass(this.tuiWrapperElement, '_invalid');
    } else {
      this.renderer.removeClass(this.tuiWrapperElement, '_invalid');
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  private setTuiWrapperElement() {
    this.intervalId = setInterval(() => {
      if (!this.tuiWrapperElement) {
        this.tuiWrapperElement =
          this.elementRef.nativeElement.querySelector('div[tuiwrapper]');
      } else {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      }
    }, 15);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
